<template>
  <div class="flex flex-col fadein m-5 min-w-16 z-10" :class="hideSidebarStyles">
    <div
      class="bg-slate-middle border-0 border-white justify-center items-center m-1 h-12 w-12 z-51 flex rounded-full cursor-pointer pointer-events-auto"
      :class="toggleButtonStyles"
      tabindex="0"
      @pointerdown="() => emit('toggleSidebar')"
      @keydown.enter="() => emit('toggleSidebar')"
    >
      <img class="absolute w-5 h-5 invert" :class="mobileViewStyles" :src="Arrow" alt="sidebar" />
    </div>
    <div class="relative mt-2 fadein overflow-y-auto overflow-x-hidden mb-9 max-height-25 block p-1">
      <div
        v-for="(board, index) in ownBoards"
        :key="index"
        tabindex="0"
        class="w-12 h-12 items-center text-slate-white rounded-full flex justify-center align-center mt-2 cursor-pointer bg-slate-middle pointer-events-auto"
        :class="{
          'regular-btn-bright': index === 0,
          'border-green-400 border-4 border-solid': board._id === selectedBoardId,
          'border-0': board._id !== selectedBoardId,
          'w-auto whitespace-nowrap p-2': board.active,
        }"
        @mouseover="board.active = index === 0 ? false : true"
        @mouseleave="board.active = false"
        @pointerdown.stop="switchBoard(board)"
        @keydown.enter.stop="switchBoard(board)"
      >
        <img v-if="index === 0" class="w-4" :src="TalkBubbleIcon" :alt="t('common')" />
        <span v-else>{{ board.active ? board.name : shortenedBoardName(board.name) }}</span>
      </div>
    </div>
    <div class="flex flex-col h-1/2 flex-grow">
      <div
        v-if="boardsAreVisible && otherBoards.length > 0"
        ref="boardScrollContainer"
        class="relative mt-2 p-1 fadein overflow-y-scroll overflow-x-hidden mb-9 max-height-50 block pointer-events-auto"
        style="touch-action: none"
        @pointerdown.stop="onScrollPointerDown"
        @pointermove.stop="onScrollPointerMove"
        @pointerup.stop="onScrollPointerUp"
        @pointercancel.stop="onScrollPointerUp"
      >
        <div
          v-for="(board, index) in otherBoards"
          tabindex="0"
          class="w-12 h-12 items-center text-slate-white rounded-full flex justify-center align-center mt-2 cursor-pointer pointer-events-auto"
          :key="index"
          :style="boardStyle(board)"
          :class="{
            'border-green-400 border-4 border-solid': board._id === selectedBoardId,
            'border-0': board._id !== selectedBoardId,
            'w-auto min-w-12 whitespace-nowrap p-2': board.active,
          }"
          @mouseover="board.active = true"
          @mouseleave="board.active = false"
          @pointerdown.stop="switchBoard(board)"
          @keydown.enter.stop="switchBoard(board)"
        >
          <span>{{ board.active ? board.name : shortenedBoardName(board.name) }}</span>
        </div>
      </div>
      <div
        v-if="userIsOwner && boardsAreVisible"
        tabindex="0"
        class="w-12 h-12 m-1 items-center text-slate-white bg-slate-middle rounded-full flex justify-center align-center mt-2 cursor-pointer pointer-events-auto"
        @pointerdown.prevent="emit('createBoard')"
        @keydown.enter.prevent="emit('createBoard')"
      >
        <img class="w-5 cursor-pointer self-center invert" :src="AddBoard" />
      </div>
      <div
        v-if="noteSelection.getters.selectedCount.value > 0"
        class="flex flex-row items-center mt-2 px-3 py-1 rounded-full bg-slate-middle text-slate-white text-sm whitespace-nowrap self-start pointer-events-auto"
        aria-live="polite"
      >
        <span class="w-2 h-2 rounded-full bg-amber-400 mr-2" aria-hidden="true"></span>
        <span>{{ t('selectedCount', { n: noteSelection.getters.selectedCount.value }) }}</span>
        <button type="button" class="ml-3 underline cursor-pointer" @pointerdown.stop @click.stop="noteSelection.actions.clearSelection()">
          {{ t('clearSelection') }}
        </button>
      </div>
      <div class="rounded-full bg-slate-middle my-5 note-border w-14">
        <div
          :class="{
            'opacity-30 pointer-events-none': scale >= 300,
            'opacity-100': scale < 300,
          }"
          class="rounded-t-lg flex justify-center items-center h-7 cursor-pointer pointer-events-auto"
          tabindex="0"
          @pointerdown.stop="zoomIn()"
          @keydown.enter.stop="zoomIn()"
        >
          <img class="w-4 self-center invert mt-2" :src="UprightArrow" :alt="t('aria-zoom-in')" />
        </div>
        <p class="text-slate-white text-md py-1 m-px font-medium text-center">{{ zoomLevelLabel }}<span class="text-xs">%</span></p>
        <div
          :class="{
            'opacity-30 pointer-events-none': scale <= 0.25,
            'opacity-100': scale > 0.25,
          }"
          class="rounded-b-lg flex justify-center items-center h-7 cursor-pointer pointer-events-auto"
          tabindex="0"
          @pointerdown.stop="zoomOut()"
          @keydown.enter.stop="zoomOut()"
        >
          <img class="-rotate-180 w-4 self-center invert mb-2" :src="UprightArrow" :alt="t('aria-zoom-out')" />
        </div>
      </div>
      <div
        v-if="!sidebarIsVisible && restrictedActionAllowed"
        class="size-14 items-center text-slate-white rounded-full flex justify-center cursor-pointer bg-tw-orange-bright pointer-events-auto"
        @pointerdown.stop="() => emit('toggleNewNoteMenu')"
        @keydown.enter="() => emit('toggleNewNoteMenu')"
      >
        <img class="w-4 h-4 cursor-pointer" :src="Add" :alt="t('aria-new-note')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { PropType, computed, nextTick, onMounted, ref, toRefs, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { PermissionLevel, ScreensizeType, zoomLevels } from '@/constants'
  import { Board, Wall } from '@/common/classes'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'
  import useFilterStore from '@/composables/filter'
  import useColorStore from '@/composables/colors'

  import useCommonStore from '@/composables/common'
  import useNoteSelection from '@/composables/noteSelection'

  import UprightArrow from '@/assets/icons/fontawesome/angle-up-solid.svg'
  import TalkBubbleIcon from '@/assets/icons/fontawesome/comment-regular.svg'
  import AddBoard from '@/assets/icons/fontawesome/layer-plus-solid.svg'
  import Add from '@/assets/icons/fontawesome/plus-solid.svg'
  import Arrow from '@/assets/icons/fontawesome/angle-left-solid.svg'
  import { DraggedNote } from '@/views/pages/WallpageView.vue'

  const messages = {
    no: {
      highlight: 'Fremhev',
      selectedCount: '{n} valgt',
      clearSelection: 'nullstill',
    },
    en: {
      highlight: 'highlight',
      selectedCount: '{n} selected',
      clearSelection: 'clear',
    },
  }
  const { t } = useI18n({ messages })

  const props = defineProps({
    wall: { type: Object as PropType<Wall>, required: true },
    restrictedActionAllowed: { type: Boolean, required: true },
    sidebarNoteBeingDragged: { type: Object as PropType<DraggedNote>, required: true },
    sidebarIsVisible: { type: Boolean, required: true },
    scale: { type: Number, required: true },
    canZoomIn: { type: Boolean, required: true },
    canZoomOut: { type: Boolean, required: true },
  })

  const emit = defineEmits<{
    (e: 'toggleNewNoteMenu'): void
    (e: 'toggleSidebar'): void
    (e: 'createBoard'): void
    (e: 'zoomToScale', amount: number): void
  }>()

  const route = useRoute()
  const filterStore = useFilterStore()
  const wallStore = useWallStore()
  const commonStore = useCommonStore()
  const noteSelection = useNoteSelection()
  const userStore = useUserStore()
  const colorStore = useColorStore()

  const { selectedBoardId } = filterStore.getters
  const { isMobileView } = commonStore.getters

  const { scale } = toRefs(props)

  onMounted(async () => {
    if (route.query.bId) {
      const board = ownBoards.value.find((b: Board) => b.name === route.query.bId)
      if (board && boardsAreVisible.value) switchBoard(board)
      else switchBoard(props.wall.boards[0]) // set default (common) board
    } else switchBoard(props.wall.boards[0]) // set default (common) board
  })

  const selectedTaskId = filterStore.getters.selectedTaskId

  const boardsAreVisible = computed(() => {
    const task = props.wall.tasks.find((t) => t._id === filterStore.getters.selectedTaskId.value)
    const allowOtherBoards = task?.allowBoardCreation ?? false
    return allowOtherBoards
  })

  const hideSidebarStyles = computed(() => {
    const mobileStyle = isMobileView.value && !props.sidebarIsVisible ? 'left-0' : ''
    const dragStyle = props.sidebarNoteBeingDragged.note ? 'opacity-0' : ''
    return `${mobileStyle} ${dragStyle}`
  })

  const toggleButtonStyles = computed(() => {
    const value = commonStore.getters.screensizeType.value === ScreensizeType.tablet ? 'left-1/3' : 'left-1/4'
    return props.sidebarIsVisible ? value : 'left-0'
  })

  const zoomLevelLabel = computed(() => {
    return Math.floor(scale.value * 100)
  })

  const userIsOwner = computed(() => {
    const user = userStore.getters.user.value
    if (user) {
      return commonStore.actions.hasMinimumPermission(user, PermissionLevel.Organize, props.wall)
    } else return false
  })

  const mobileViewStyles = computed(() => {
    return props.sidebarIsVisible ? '' : '-rotate-180'
  })

  function boardStyle(board: Board) {
    const backgroundColor = `${colorStore.actions.selectColor(board.name, board._id).background}`
    const color = `${colorStore.actions.selectColor(board.name, board._id).text}`
    return {
      'background-color': backgroundColor,
      color: color.toString(),
    }
  }

  const ownBoards = computed(() => {
    const boardsVisible = boardsAreVisible.value
    const user = userStore.getters.user.value
    if (user) {
      let boards: Board[] = Array.from(props.wall.boards) || []
      const defaultBoard = boards.shift()
      if (!boardsVisible && defaultBoard) {
        return [defaultBoard] // return the default (common) board as the only option
      }
      boards = boards.filter((board) => board.name.includes(user.profile.username))
      boards = boards.sort((c: Board, n: Board) => {
        return c.name.charCodeAt(0) > n.name.charCodeAt(0) ? 1 : -1
      }) // sort alphabetically
      if (defaultBoard) boards.unshift(defaultBoard)
      return boards
    } else return []
  })

  /*  watch(ownBoards, () => {
    if (ownBoards.value.length) switchBoard(ownBoards.value[0])
  }) */

  const otherBoards = computed(() => {
    let boards: Board[] = Array.from(props.wall.boards) || []
    boards = boards.filter((board) => !ownBoards.value.some((ob) => ob._id === board._id)) // remove own boards from selection
    boards = boards.sort((c: Board, n: Board) => {
      return c.name.charCodeAt(0) > n.name.charCodeAt(0) ? 1 : -1
    })
    return boards
  })

  function shortenedBoardName(name: string) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
  }

  function switchBoard(board: Board) {
    if (board && board._id) {
      wallStore.actions.setSelectedBoard(board)
      filterStore.actions.setBoardId(board._id)
      setTimeout(() => (board.active = false), 2000)
    }
  }

  const boardScrollContainer = ref<HTMLElement | null>(null)
  let scrollDragState: { pointerId: number; startY: number; startScrollTop: number } | null = null

  function onScrollPointerDown(e: PointerEvent) {
    boardScrollContainer.value?.setPointerCapture(e.pointerId)
    scrollDragState = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startScrollTop: boardScrollContainer.value?.scrollTop ?? 0,
    }
  }

  function onScrollPointerMove(e: PointerEvent) {
    if (!scrollDragState || e.pointerId !== scrollDragState.pointerId) return
    const delta = scrollDragState.startY - e.clientY
    if (boardScrollContainer.value) {
      boardScrollContainer.value.scrollTop = scrollDragState.startScrollTop + delta
    }
  }

  function onScrollPointerUp(e: PointerEvent) {
    if (scrollDragState && e.pointerId === scrollDragState.pointerId) {
      boardScrollContainer.value?.releasePointerCapture(e.pointerId)
      scrollDragState = null
    }
  }

  function zoomIn() {
    const nextScaleValue = Math.floor(scale.value / 0.25) * 0.25 + 0.25
    if (nextScaleValue <= 300) emit('zoomToScale', nextScaleValue)
  }

  function zoomOut() {
    const nextScaleValue = Math.floor(scale.value / 0.25) * 0.25 - 0.25
    if (nextScaleValue >= 0.25) emit('zoomToScale', nextScaleValue)
  }
</script>

<style scoped></style>
